import {
  Button,
  Modal,
  Typography,
  Upload,
  message,
  Form,
  Input,
  Select,
} from "antd";
import { RcFile, UploadFile } from "antd/es/upload";
import { UploadOutlined } from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useGetPresignedUrl } from "~/services/upload";
import { CreateFileDto, useCreateFile } from "~/services/file";
import { DocumentStatus, DocumentType, ClientStatus } from "~/utils/constants";
import { useGetClients } from "~/services/client/client.queries"; // Updated import
import { useGetServiceTickets } from "~/services/service-ticket";

const { Text } = Typography;
const { Option } = Select;

interface DocumentFile extends RcFile {
  url?: string;
  preview?: string;
  originFileObj?: RcFile;
}

interface DocumentUploadModalProps {
  visible: boolean;
  documentType: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface DocumentFormValues {
  title: string;
  type: DocumentType;
  clientId?: string;
  serviceTicketId?: string;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  visible,
  documentType,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<DocumentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const getPresignedUrlMutation = useGetPresignedUrl();
  const createFileMutation = useCreateFile();

  useEffect(() => {
    if (visible && documentType) {
      form.setFieldsValue({
        type: documentType,
      });
    }
  }, [visible, documentType, form]);

  const handleUploadChange = ({ fileList }: any) => {
    // Only keep the last file (replacing previous selections)
    const latestFile = fileList.slice(-1);

    // Validate file type
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const filteredFiles = latestFile.filter((file: any) => {
      const isValidType = validTypes.includes(file.type);
      if (!isValidType) {
        message.error(
          "Please upload a valid document file (PDF, DOCX, JPG, PNG)"
        );
      }
      return isValidType;
    });

    setFileList(filteredFiles);
  };

  const handleSubmit = async (values: DocumentFormValues) => {
    if (fileList.length === 0) {
      message.error("Please select a document to upload");
      return;
    }

    setIsUploading(true);

    try {
      const file = fileList[0].originFileObj;
      if (!file) throw new Error("File not found");

      // Step 1: Get presigned URL from server
      const presignedUrlData = await getPresignedUrlMutation.mutateAsync({
        fileName: file.name,
        mimeType: file.type,
      });

      // Step 2: Upload file directly to storage using presigned URL
      await axios.put(presignedUrlData.presignedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      // Step 3: Create file record in database with the uploaded file URL
      const fileData: CreateFileDto = {
        title: values.title,
        type: values.type,
        status: DocumentStatus.SENT, // Default status
        filePath: presignedUrlData.url,
      };

      if (values.clientId && values.clientId !== "placeholder") {
        fileData.clientId = values.clientId;
      }

      if (values.serviceTicketId && values.serviceTicketId !== "placeholder") {
        fileData.serviceTicketId = values.serviceTicketId;
      }

      await createFileMutation.mutateAsync(fileData);
      message.success("Document uploaded successfully");
      setFileList([]);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error("Failed to upload document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Upload Document"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: documentType,
        }}
      >
        <Form.Item
          name="title"
          label="Document Title"
          rules={[{ required: true, message: "Please enter document title" }]}
        >
          <Input placeholder="Enter document title" />
        </Form.Item>

        <Form.Item
          name="type"
          label="Document Type"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value={DocumentType.INVENTORY}>Inventory</Option>
            <Option value={DocumentType.ADMINISTRATIVE}>Administrative</Option>
            <Option value={DocumentType.LEGAL}>Legal</Option>
            <Option value={DocumentType.SUPPLEMENTARY}>Supplementary</Option>
            <Option value={DocumentType.OTHER}>Other</Option>
          </Select>
        </Form.Item>

        <SelectClientAndServiceTicket />

        <Form.Item
          label="Document File"
          rules={[{ required: true, message: "Please upload a document file" }]}
        >
          <Upload
            beforeUpload={() => false}
            onChange={handleUploadChange}
            fileList={fileList as UploadFile[]}
            maxCount={1}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
          <Text type="secondary" className="mt-2 block">
            Supported formats: PDF, DOCX, JPG, PNG (Max: 10MB)
          </Text>
        </Form.Item>

        <Form.Item>
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isUploading}
              disabled={fileList.length === 0}
            >
              Upload Document
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SelectClientAndServiceTicket = () => {
  const [selectedClientId, setSelectedClientId] = useState<
    string | undefined
  >();
  const form = Form.useFormInstance();

  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  const { data: serviceTickets } = useGetServiceTickets({
    clientId: selectedClientId,
    pageSize: 100,
  });

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    // Clear service ticket selection when client changes
    form.setFieldValue("serviceTicketId", undefined);
  };

  return (
    <>
      <Form.Item name="clientId" label="Client (Optional)">
        <Select
          placeholder="Select client"
          allowClear
          showSearch
          optionFilterProp="children"
          loading={clientsLoading}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleClientChange}
          options={clients?.items.map((item) => ({
            value: item.id,
            label: `${
              item.companyName ??
              ([item.firstName, item.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
                "Unnamed Client")
            } - ${item.email}`,
          }))}
        />
      </Form.Item>

      <Form.Item name="serviceTicketId" label="Service Ticket (Optional)">
        <Select
          placeholder="Select service ticket"
          allowClear
          showSearch
          optionFilterProp="children"
          disabled={!selectedClientId}
          notFoundContent={
            !selectedClientId
              ? "Please select a client first"
              : "No service tickets found"
          }
        >
          {serviceTickets?.items?.map((ticket) => (
            <Option key={ticket.id} value={ticket.id}>
              {ticket.serial} - {ticket.serviceType}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </>
  );
};
